import { IAppState } from 'src/app/store/state/app.state';
import { getSignInState } from 'src/app/store/auth/selectors/auth.selectors';
import { Store } from '@ngrx/store';
import * as AuthActions from './../../store/auth/actions/auth.actions';
import * as SystemActions from 'src/app/store/system/actions/system.actions';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { Injectable, signal } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CustomQueryEncoderHelper } from './../../utils/custom-http-encoder';
import { ChangedPasswordVM, IClaim, IdentityUser, ManagerAllowBuySell, ManagerAllowingInvdividualReport, ManagerAllowWithdrawals, ManagerIDs } from '../../models/auth';
import { BaseHttpService } from './base.http.service';
import { Router } from '@angular/router';
import { AppConfig } from 'src/app/app.config';
import { BehaviorSubject, mergeMap, Observable, of, throwError } from "rxjs";
import { catchError, distinctUntilChanged, map, take, tap } from "rxjs/operators";
import { IJwtoken } from 'src/app/models/jwtoken';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Features } from 'src/app/models/features';
import { IEnvironmentConfig, IEnvironmentSettings } from 'src/app/models/environmentConfig';
import {
  EntityRelationListingItem,
  LegalEntityGroupRelationListingItem,
  UserRelationshipsApiModel
} from "../../models/userRelationships";
import { UserRelationshipService } from "./user-relationship.service";
import { UserRoleType } from "../../models/userRoleTypes";

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

@Injectable()
export class AuthService extends BaseHttpService {
  private isPostLoginConfigLoaded = false;

  // A user is only considered 'logged in' when they have an assumed user
  private isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isLoggedIn$: Observable<boolean> = this.isLoggedInSubject.asObservable();

  // Used for notifying of changes to the authentication token
  private authTokenSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  public authToken$: Observable<string> = this.authTokenSubject.asObservable();

  // Records the username of the authenticated identity
  private usernameSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  public username$: Observable<string> = this.usernameSubject.asObservable();

  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }
  get env(): IEnvironmentSettings {
    return this.appConfig.getEnvironment();
  }

  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private router: Router,
    private featureToggleService: FeatureToggleService,
    private store: Store<IAppState>,
    private jwtHelper: JwtHelperService,
    private userRelationshipService: UserRelationshipService) {

    super();
    this.username$.pipe(distinctUntilChanged()) // Only need to get users when the identity changes
      .subscribe((username) => {
        if (username) {
          this.store.dispatch(new AuthActions.GetIdentityUsers());
        }
      });

    this.authToken$.subscribe(token => {
      if(token == null){
        this.userRelationships.set(null);
      }
    });
  }

  public login(username: string, password: string, tfaCode?: string): Observable<boolean> {
    const payload: any = {
      grant_type: "password",
      client_id: this.env.clientId,
      managerId: this.env.managerEntityId,
      username: username,
      password: password
    };
    if (tfaCode) {
      // Only add passcode to payload if performing Two Factor Authentication
      payload.passcode = tfaCode;
    }
    const httpParams = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
      fromObject: payload
    });

    return this.http
        .post(this.envSettings.loginApiUrl, httpParams, {
            observe: 'body',
            responseType: 'json',
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        })
        .pipe(
            mergeMap((result: IJwtoken) => {
                if (result && result.access_token) {
                    return this.onLogin(result)
                        .pipe(map(() => {
                          return true;
                        }));
                }
                return of(false);
            }),
        );
  }
  
  private setupUserRelationships() : Observable<void> {

    const claims = this.jwtHelper.decodeToken(localStorage.getItem("token"));

    if (!!claims["primarysid"]) {
      return this.userRelationshipService.getUserRelationships()
          .pipe(tap((res) => {
            this.userRelationships.set(res);
          }), map(() => void 0) );
    }
    return of(void 0);
  }

  public exchangeCodeForTokens(code: string, codeVerifier: string): Observable<IJwtoken> {
    const body = {
      code: code,
      codeVerifier: codeVerifier,
      clientId: this.appConfig.getEnvironment().clientId,
      managerEntityId: +this.appConfig.getEnvironment().managerEntityId,
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.appConfig.getEnvironmentConfig().loginApiCognitoUrl, body, { headers })
      .pipe(
        map(response => ({
          access_token: response.accessToken,
          refresh_token: response.refreshToken || '',
          expires_in: response.expiresIn || 0,
          token_type: response.tokenType || 'Bearer',
        })),
        catchError(error => throwError(() => new Error('Error fetching tokens')))
      );
  }

  public loginWithAssumedUser(userId: number, tfaCode?: string): Observable<boolean> {
    const token = JSON.parse(localStorage.getItem('token'));
    const payload: any = {
      grant_type: 'assume_user',
      client_id: this.env.clientId,
      token: token.access_token,
      user_id: userId
    };
    if (tfaCode) {
      // Only add passcode to payload if performing Two Factor Authentication
      payload.passcode = tfaCode;
    }
    const httpParams = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
      fromObject: payload
    });

    return this.http.post(this.envSettings.loginApiUrl, httpParams, {
      observe: 'body',
      responseType: 'json',
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    })
      .pipe(
        mergeMap((result: IJwtoken) => {
          if (result && result.access_token) {
            return this.onLogin(result)
                .pipe(map(() => {
                  return true;
                }));
          }
          return of(false);
        })
      );
  }

  public getUsers(): Observable<IdentityUser[]> {
    return this.http.get<IdentityUser[]>(`${this.envSettings.loginApiUrlBase}/api/identity/users`, {
      observe: 'body',
      responseType: 'json',
      headers: this.getHttpHeaders()
    });
  }

  public refreshToken() {
    const paramsObject = {
      client_id: this.env.clientId,
      managerId: this.env.managerEntityId,
      grant_type: "refresh_token",
      refresh_token: this.getRefreshToken()
    };

    const httpParams = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
      fromObject: paramsObject
    });

    return this.http.post(this.envSettings.loginApiUrl, httpParams, {
      observe: 'body',
      responseType: 'json',
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      map((result: IJwtoken) => {
        if (result && result.access_token) {
          this.setTokenAndGetRelationships(result);
        }
        return result;
      })
    );
  }

  public onLogin(token: IJwtoken): Observable<any> {
    // First, ensure we are 'logged out' to prepare for the new session
    this.isLoggedInSubject.next(false);

    return this.store.select(getSignInState)
      .pipe(take(1),
          mergeMap(signInState => {
        this.store.dispatch(new SystemActions.ClearSystemState());
        this.store.dispatch(new AuthActions.SetIdentityUsers(signInState.identityUsers)); // Preserve the list of IdentityUsers
        return this.setTokenAndGetRelationships(token);
      }));
  }

  onCognitoLogin(token: IJwtoken): Observable<boolean> {
   return this.onLogin(token)
       .pipe(map(() => {
         return true;
       }));
  }

  private setTokenAndGetRelationships(token: IJwtoken): Observable<void> {
    localStorage.removeItem('token');
      localStorage.setItem('token', JSON.stringify(token));
    return this.setupUserRelationships().pipe(tap(() => {
      this.updateAuthSubjects(this.getAuthToken());
      this.loadPostLoginClientConfig();
    }));
  }

  public logout() {
    this.isPostLoginConfigLoaded = false;
    localStorage.removeItem('token');

    const useUniversalLogin = this.appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled;
    if (useUniversalLogin) {
      this.clearCookie('identityServerAccessToken');
      this.clearCookie('identityServerRefreshToken');
      this.clearCookie('identityServerExpiresIn');
    }
    this.updateAuthSubjects(null);
    this.store.dispatch(new AuthActions.LogoutSuccess());
    this.router.navigate(['/auth/signin']);
  }

  public logout$() {
    this.isPostLoginConfigLoaded = false;
    localStorage.removeItem('token');

    const useUniversalLogin = this.appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled;
    if (useUniversalLogin) {
      this.clearCookie('identityServerAccessToken');
      this.clearCookie('identityServerRefreshToken');
      this.clearCookie('identityServerExpiresIn');
    }
    
    this.store.dispatch(new AuthActions.LogoutSuccess());
    this.updateAuthSubjects(null);
    return of(true);
  }

  public requestPasswordReset(httpParams) {
    let newHttpParams = new HttpParams();
    httpParams.updates.forEach(update => {
      newHttpParams = newHttpParams.set(update.param, update.value);
    });
    return this.http.post(this.envSettings.webApiUrl + 'password/reset', newHttpParams, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    });
  }

  public resetPassword(httpParams) {
    let newHttpParams = new HttpParams();
    httpParams.updates.forEach(update => {
      newHttpParams = newHttpParams.set(update.param, update.value);
    });
    return this.http.post(this.envSettings.webApiUrl + 'password', newHttpParams, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    });
  }

  public changePassword(vm: ChangedPasswordVM) {
    return this.http.post(this.envSettings.webApiUrl + 'currentUser/password', vm, {
      observe: 'body',
      responseType: 'json',
      headers: this.getHttpHeaders()
    });
  }

  public getAuthToken(): string {
    const token = JSON.parse(localStorage.getItem('token'));
    return (token != null) ? token.access_token : '';
  }

  public getRefreshToken(): string {
    const token = JSON.parse(localStorage.getItem('token'));
    return (token != null) ? token.refresh_token : '';
  }

  public isLoggedIn(): boolean {
    /**
     * When user is logged in and refreshes the page, post login config needs to be loaded again
     * also, the isLoggedInSubject needs to be updated, otherwise it seems user is nit logged in, since this subject only gets updated when a user logs in or when token is refreshed
     */
    if (!this.isPostLoginConfigLoaded && !!localStorage.getItem('token')) {
      this.loadPostLoginClientConfig();
      this.updateAuthSubjects(this.getAuthToken());
    }
    return !!localStorage.getItem('token');
  }

  private loadPostLoginClientConfig() {
    // This function is the holding area that we will use to call a service to go and load the investors level config
    // The below at the moment is only here until we know what that service look like and all of the information that it needs to contain.

    const advisorId = this.getManagerIdFromJwt(this.getAuthToken());

    // reports
    this.enableReportsForIndividuals(advisorId);

    // buysell
    if (advisorId && Object.values(ManagerAllowBuySell).includes(Number(advisorId))) {
      this.appConfig.setIndividualBuySellEnabled(true);
    } else {
      this.appConfig.setIndividualBuySellEnabled(false);
    }

    if (advisorId) {
      this.appConfig.setWithdrawalsEnabledForManagerUsers(Object.values(ManagerAllowWithdrawals).includes(Number(advisorId)));
    }

    this.isPostLoginConfigLoaded = true;
  }

  private enableReportsForIndividuals(advisorId: number) {
    const isReportsEnabledForIndividuals = this.featureToggleService.featureIndividualInvestorOn(Features.REPORTS);

    if (isReportsEnabledForIndividuals) {
      return;
    }

    const isManagerAllowingIndividualReports = advisorId && Object.values(ManagerAllowingInvdividualReport).includes(Number(advisorId));

    this.appConfig.setAllowIndividualConfig(isManagerAllowingIndividualReports);
  }

  public hasFalseInception(): boolean {
    // if advisor is Rival, then set hasFalseInception to true. make amendments in performance section
    const advisorId = this.getManagerIdFromJwt(this.getAuthToken());
    if (advisorId && Number(advisorId) === ManagerIDs.RIVAL) {
      return true;
    }
    return false;
  }

  private getManagerIdFromJwt(access_token: string): number {
    let decodedClaim: IClaim;
    if (access_token) {
      decodedClaim = this.jwtHelper.decodeToken(access_token);
    }
    return (decodedClaim && decodedClaim['primarygroupsid']) ? decodedClaim['primarygroupsid'] : null;
  }

  private updateAuthSubjects(authToken: string | null) {
    if (authToken) {
      const claims = this.jwtHelper.decodeToken(authToken);

      // A person is only 'logged in' if they have assumed a user (the Id being contained in the 'primarysid' claim)
      this.isLoggedInSubject.next(!!claims['primarysid']);
      this.usernameSubject.next(claims['unique_name']);
    } else {
      this.isLoggedInSubject.next(false);
      this.usernameSubject.next(null);
    }
    this.authTokenSubject.next(authToken);
  }

  public getManagerId(): number {
    return this.getManagerIdFromJwt(this.getAuthToken());
  }
  
  public userRelationships = signal<UserRelationshipsApiModel>(null);
  

  public getFirstEntityRelationship(): EntityRelationListingItem {
    let userRelationships = this.userRelationships();

    if(!userRelationships)
      return null;

    if(userRelationships.entityRelationships?.length > 0)
      return userRelationships.entityRelationships[0];

    return null;
  }

  public getUserRelationships(): (EntityRelationListingItem | LegalEntityGroupRelationListingItem)[] {
    let userRelationships = this.userRelationships();

    if(!userRelationships)
      return null;

    return [...userRelationships.entityRelationships, ...userRelationships.legalEntityGroupRelationships];
  }
  
  public getUserEntityRelationships(): EntityRelationListingItem[] {
    return this.userRelationships()?.entityRelationships;
  }

  public doesUserHaveRole(userRoleType: UserRoleType) {

    let userRelationships = this.getUserRelationships();

    if(userRelationships == null)
      return undefined;

    let rolesUserHas = userRelationships.map(r => r.userRole);
    return rolesUserHas.includes(userRoleType);
  }
  
  public doesUserHaveRoleObservable(userRoleType: UserRoleType): Observable<boolean> {
      let relationships = this.getUserRelationships();
      
      if(relationships == null){
        return this.setupUserRelationships()
            .pipe(map(() => {
              relationships = this.getUserRelationships();

              let rolesUserHas = relationships.map(r => r.userRole);
              return rolesUserHas.includes(userRoleType);
            }));
      }
      else
      {

        let rolesUserHas = relationships.map(r => r.userRole);

        return of(rolesUserHas.includes(userRoleType));   
    }
  }

  public getCookie(name: string): string | null {
    const useUniversalLogin = this.appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled;
    if (useUniversalLogin) {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number): void {
    const useUniversalLogin = this.appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled;
    if (useUniversalLogin) {
      const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; secure; SameSite=None; expires=${expires}`;
    }
  }

  private clearCookie(name: string): void {
    const useUniversalLogin = this.appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled;
    if (useUniversalLogin) {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; SameSite=None`;
    }
  }
}
